/**
 * Report Engine
 * 
 * Executes report definitions by:
 * 1. Running core queries (with showPrivate=true, conditional skip support)
 * 2. Injecting optional field names into query select lists
 * 3. Running additional queries only when their columns are enabled
 * 4. Pre-aggregating joined data for related object queries
 * 5. Enriching RECORD_URLs by re-querying per entity for correct links
 * 6. Passing everything to the report's process() function
 */

import { fetchIntacctData, fetchMoreIntacctData } from '../utils/api';

/**
 * Fetch all pages for a single query.
 * showPrivate=true by default so private entity records are included.
 */
const fetchAllPages = async (config, object, fieldsString, filter, onProgress, showPrivate = true) => {
  const PAGE_SIZE = 2000;
  
  if (onProgress) onProgress(`Querying ${object}...`);
  
  const firstPage = await fetchIntacctData(config, object, fieldsString, filter, PAGE_SIZE, 0, showPrivate);
  let allData = [...(firstPage.data || [])];
  const totalCount = firstPage.metadata?.totalcount || allData.length;
  
  let offset = allData.length;
  while (offset < totalCount) {
    if (onProgress) onProgress(`Fetching ${object}: ${allData.length} / ${totalCount}...`);
    const morePage = await fetchMoreIntacctData(config, object, fieldsString, filter, PAGE_SIZE, offset, showPrivate);
    if (morePage.data?.length > 0) {
      allData = [...allData, ...morePage.data];
      offset += morePage.data.length;
    } else {
      break;
    }
  }
  
  return allData;
};

/**
 * Enrich RECORD_URLs by re-querying per entity.
 * 
 * When querying at top level with showPrivate=true, Intacct returns records
 * from all entities but RECORD_URLs point to top-level context and don't work
 * for entity-level records. By re-querying with locationId set per entity,
 * Intacct returns entity-scoped RECORD_URLs that work correctly.
 */
const enrichRecordUrls = async (config, datasets, queryDefs, onProgress) => {
  // Only enrich if user is NOT already connected at entity level
  if (config.locationId && config.locationId.trim()) return;

  for (const query of queryDefs) {
    const data = datasets[query.id];
    if (!data || data.length === 0) continue;

    const fields = query.fields || [];
    const hasRecordUrl = fields.includes('RECORD_URL');
    const hasMegaEntity = fields.includes('MEGAENTITYID');

    // Check for dot-notation (e.g., APBILL.RECORD_URL on APBILLITEM)
    const dotUrlField = fields.find(f => f.endsWith('.RECORD_URL') && f !== 'RECORD_URL');
    const dotEntityField = fields.find(f => f.endsWith('.MEGAENTITYID') && f !== 'MEGAENTITYID');

    // Enrich direct RECORD_URL
    if (hasRecordUrl && hasMegaEntity) {
      await enrichDirectUrls(config, data, query, onProgress);
    }

    // Enrich dot-notation RECORD_URL
    if (dotUrlField && dotEntityField) {
      const parentObj = dotUrlField.split('.')[0];
      await enrichDotUrls(config, data, parentObj, dotEntityField, dotUrlField, onProgress);
    }
  }
};

/**
 * Enrich RECORD_URLs for records with MEGAENTITYID on the same object.
 */
const enrichDirectUrls = async (config, data, query, onProgress) => {
  const entityGroups = {};
  for (const rec of data) {
    const eid = String(rec.MEGAENTITYID || '').trim();
    if (!eid) continue;
    if (!entityGroups[eid]) entityGroups[eid] = [];
    entityGroups[eid].push(rec);
  }

  const entities = Object.keys(entityGroups);
  if (entities.length === 0) return;

  if (onProgress) onProgress(`Enriching ${query.object} URLs for ${entities.length} entity(s)...`);

  for (const entityId of entities) {
    try {
      const entityConfig = { ...config, locationId: entityId };
      const filter = query.buildFilter
        ? query.buildFilter(query._params || {})
        : 'RECORDNO > 0';

      const entityData = await fetchAllPages(
        entityConfig, query.object, 'RECORDNO,RECORD_URL', filter, null, false
      );

      const urlMap = {};
      for (const rec of entityData) {
        const rn = String(rec.RECORDNO || '').trim();
        if (rn && rec.RECORD_URL) urlMap[rn] = rec.RECORD_URL;
      }

      let patched = 0;
      for (const rec of entityGroups[entityId]) {
        const rn = String(rec.RECORDNO || '').trim();
        if (urlMap[rn]) { rec.RECORD_URL = urlMap[rn]; patched++; }
      }

      if (onProgress) onProgress(`  ${entityId}: ${patched} ${query.object} URL(s) enriched`);
    } catch (err) {
      console.warn(`URL enrichment failed for ${query.object} entity ${entityId}:`, err.message);
    }
  }
};

/**
 * Enrich dot-notation RECORD_URLs (e.g., APBILL.RECORD_URL on APBILLITEM).
 */
const enrichDotUrls = async (config, data, parentObject, entityField, urlField, onProgress) => {
  const parentRnField = `${parentObject}.RECORDNO`;
  const entityGroups = {};

  for (const rec of data) {
    const eid = String(rec[entityField] || '').trim();
    const parentRn = String(rec[parentRnField] || '').trim();
    if (!eid || !parentRn) continue;
    if (!entityGroups[eid]) entityGroups[eid] = new Set();
    entityGroups[eid].add(parentRn);
  }

  const entities = Object.keys(entityGroups);
  if (entities.length === 0) return;

  if (onProgress) onProgress(`Enriching ${parentObject} URLs for ${entities.length} entity(s)...`);

  for (const entityId of entities) {
    try {
      const entityConfig = { ...config, locationId: entityId };
      const parentRns = [...entityGroups[entityId]];
      const urlMap = {};

      // Batch to avoid long filters
      const BATCH = 200;
      for (let i = 0; i < parentRns.length; i += BATCH) {
        const batch = parentRns.slice(i, i + BATCH);
        const filter = `RECORDNO IN (${batch.map(r => `'${r}'`).join(',')})`;
        const entityData = await fetchAllPages(
          entityConfig, parentObject, 'RECORDNO,RECORD_URL', filter, null, false
        );
        for (const rec of entityData) {
          const rn = String(rec.RECORDNO || '').trim();
          if (rn && rec.RECORD_URL) urlMap[rn] = rec.RECORD_URL;
        }
      }

      let patched = 0;
      for (const rec of data) {
        const parentRn = String(rec[parentRnField] || '').trim();
        if (urlMap[parentRn]) { rec[urlField] = urlMap[parentRn]; patched++; }
      }

      if (onProgress) onProgress(`  ${entityId}: ${patched} ${parentObject} URL(s) enriched`);
    } catch (err) {
      console.warn(`URL enrichment failed for ${parentObject} entity ${entityId}:`, err.message);
    }
  }
};

/**
 * Pre-aggregate data from an additional (joined) query.
 */
const aggregateJoinedData = (childData, queryDef, enabledFieldIds) => {
  const { joinOn, aggregation = 'list' } = queryDef;
  const grouped = {};

  for (const record of childData) {
    const key = String(record[joinOn.child] || '').trim();
    if (!key) continue;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(record);
  }

  const result = {};
  for (const [key, records] of Object.entries(grouped)) {
    const aggregated = {};
    for (const fieldId of enabledFieldIds) {
      const values = records
        .map(r => String(r[fieldId] || '').trim())
        .filter(v => v !== '');

      switch (aggregation) {
        case 'first':
          aggregated[fieldId] = values[0] || '';
          break;
        case 'sum':
          aggregated[fieldId] = values.reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
          break;
        case 'list':
        default:
          aggregated[fieldId] = [...new Set(values)].join(', ');
          break;
      }
    }
    result[key] = aggregated;
  }

  return result;
};

/**
 * Execute a report definition with optional column support
 */
export const executeReport = async (config, reportDef, params, enabledOptionalIds = [], onProgress) => {
  const optionalColumns = reportDef.optionalColumns || [];
  const additionalQueries = reportDef.additionalQueries || [];

  const enabledColumns = optionalColumns.filter(col => enabledOptionalIds.includes(col.id));

  // Group enabled columns by queryId
  const columnsByQuery = {};
  for (const col of enabledColumns) {
    if (!columnsByQuery[col.queryId]) columnsByQuery[col.queryId] = [];
    columnsByQuery[col.queryId].push(col);
  }

  // Step 1: Run core queries (showPrivate=true, conditional check, optional field injection)
  const datasets = {};
  const executedQueries = [];

  for (const query of reportDef.queries) {
    if (query.conditional && !query.conditional(params)) {
      if (onProgress) onProgress(`Skipping ${query.object} (not needed for current parameters)`);
      datasets[query.id] = [];
      continue;
    }

    const extraFields = (columnsByQuery[query.id] || []).map(c => c.id);
    const allFields = [...new Set([...query.fields, ...extraFields])];
    const filter = query.buildFilter ? query.buildFilter(params) : (query.filter || 'RECORDNO > 0');
    const fieldsString = allFields.join(',');

    const queryData = await fetchAllPages(config, query.object, fieldsString, filter, onProgress);
    datasets[query.id] = queryData;

    // Store for URL enrichment
    executedQueries.push({ ...query, _params: params });

    if (onProgress) onProgress(`${query.object}: ${queryData.length} records loaded`);
  }

  // Step 1.5: Enrich RECORD_URLs per entity for correct hyperlinks
  await enrichRecordUrls(config, datasets, executedQueries, onProgress);

  // Step 2: Run additional queries only if they have enabled columns
  const aggregatedJoins = {};

  for (const addQuery of additionalQueries) {
    const enabledFieldsForQuery = (columnsByQuery[addQuery.id] || []).map(c => c.id);
    if (enabledFieldsForQuery.length === 0) continue;

    const allFields = [...new Set([...addQuery.coreFields, ...enabledFieldsForQuery])];
    const filter = addQuery.buildFilter ? addQuery.buildFilter(params) : (addQuery.filter || 'RECORDNO > 0');
    const fieldsString = allFields.join(',');

    if (onProgress) onProgress(`Querying ${addQuery.object} for additional columns...`);
    const queryData = await fetchAllPages(config, addQuery.object, fieldsString, filter, onProgress);

    if (onProgress) onProgress(`${addQuery.object}: ${queryData.length} records loaded, aggregating...`);

    aggregatedJoins[addQuery.id] = aggregateJoinedData(queryData, addQuery, enabledFieldsForQuery);
    datasets[addQuery.id] = queryData;
  }

  // Step 3: Process
  if (onProgress) onProgress('Processing report...');
  const result = reportDef.process(datasets, params, enabledColumns, aggregatedJoins);

  return {
    columns: result.columns,
    data: result.data,
    summary: result.summary || null,
    formatRules: result.formatRules || reportDef.formatRules || {},
    detailRowIndices: result.detailRowIndices || null,
    groupedColumns: result.groupedColumns || null,
    rowOutlineLevels: result.rowOutlineLevels || null,
  };

  
};
// ==========================================================================
// ADD THIS ENTIRE BLOCK to the bottom of reportEngine.js, BEFORE the closing
// of the file (it's all exports, so just add it after executeReport).
// Also add it to the export: export { executeReport, executeWorkbook };
// ==========================================================================

/**
 * Execute a workbook definition (multi-sheet, shared queries).
 *
 * Runs the consolidated query set once, enriches URLs, then calls each
 * sheet's report process() with mapped datasets and parameters.
 *
 * @param {Object} config - Intacct connection config
 * @param {Object} workbookDef - The workbook definition (projectWorkbook.js)
 * @param {Object} params - User-supplied parameter values
 * @param {Function} onProgress - Status callback: (message) => void
 * @returns {Array} Array of { sheetName, columns, data, formatRules, detailRowIndices, groupedColumns, rowOutlineLevels, detailLevel, summary }
 */
export const executeWorkbook = async (config, workbookDef, params, onProgress) => {
  // Step 1: Run all consolidated queries
  const datasets = {};
  const executedQueries = [];

  const totalQueries = workbookDef.queries.filter(q => !q.conditional || q.conditional(params)).length;
  let queryCount = 0;

  for (const query of workbookDef.queries) {
    if (query.conditional && !query.conditional(params)) {
      if (onProgress) onProgress('Skipping ' + query.object + ' (not needed)');
      datasets[query.id] = [];
      continue;
    }

    queryCount++;
    const filter = query.buildFilter ? query.buildFilter(params) : (query.filter || 'RECORDNO > 0');
    const fieldsString = query.fields.join(',');

    if (onProgress) onProgress('Query ' + queryCount + '/' + totalQueries + ': ' + query.object + '...');

    const queryData = await fetchAllPages(config, query.object, fieldsString, filter, onProgress);
    datasets[query.id] = queryData;

    executedQueries.push({ ...query, _params: params });

    if (onProgress) onProgress(query.object + ': ' + queryData.length + ' records');
  }

  // Step 2: Enrich RECORD_URLs per entity
  if (onProgress) onProgress('Enriching URLs...');
  await enrichRecordUrls(config, datasets, executedQueries, onProgress);

  // Step 3: Process each sheet
  const sheetResults = [];

  for (const sheetDef of workbookDef.sheets) {
    if (onProgress) onProgress('Processing: ' + sheetDef.name + '...');

    try {
      const mappedDatasets = sheetDef.mapDatasets(datasets);
      const mappedParams = sheetDef.mapParams(params);

      // Call the real report's process() function
      const result = sheetDef.report.process(mappedDatasets, mappedParams);

      sheetResults.push({
        sheetName: sheetDef.name,
        columns: result.columns || [],
        data: result.data || [],
        formatRules: result.formatRules || sheetDef.report.formatRules || {},
        detailRowIndices: result.detailRowIndices || [],
        groupedColumns: result.groupedColumns || [],
        rowOutlineLevels: result.rowOutlineLevels || [],
        detailLevel: result.detailLevel,
        summary: result.summary || null,
      });

      const rowCount = (result.data || []).length;
      if (onProgress) onProgress(sheetDef.name + ': ' + rowCount + ' rows');
    } catch (err) {
      console.error('Sheet processing failed: ' + sheetDef.name, err);
      if (onProgress) onProgress('WARNING: ' + sheetDef.name + ' failed: ' + err.message);
      // Push an empty sheet so the workbook still generates
      sheetResults.push({
        sheetName: sheetDef.name + ' (ERROR)',
        columns: ['Error'],
        data: [{ 'Error': 'Sheet generation failed: ' + err.message }],
        formatRules: {},
        detailRowIndices: [],
        groupedColumns: [],
        rowOutlineLevels: [],
      });
    }
  }

  return sheetResults;
};


